import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Bell, Search, ArrowDown, Globe, ArrowUp, CreditCard, MoreHorizontal } from 'lucide-react';
import { Screen } from '../../types';
import { PhoneFrame } from '../PhoneFrame';

interface AccountHomeScreenProps {
  screen: Screen;
  onNext?: () => void;
}

const iconMap = {
  'arrow-down': ArrowDown,
  'globe': Globe,
  'arrow-up': ArrowUp,
  'credit-card': CreditCard,
  'more-horizontal': MoreHorizontal
};

export const AccountHomeScreen: React.FC<AccountHomeScreenProps> = ({ screen, onNext }) => {
  return (
    <div className="min-h-screen flex items-center justify-center px-8 py-16">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        {/* Left Side - Mobile Account Home */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="flex justify-center"
        >
          <PhoneFrame>
            <div className="p-6 h-full flex flex-col">
              {/* Top Navigation */}
              <div className="flex items-center justify-between mb-6">
                <ArrowLeft className="w-5 h-5 text-gray-600" />
                <div className="flex items-center space-x-3">
                  <Bell className="w-5 h-5 text-gray-600" />
                  <Search className="w-5 h-5 text-gray-600" />
                </div>
              </div>

              {/* Primary Account */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="bg-blue-50 rounded-lg p-4 mb-6"
              >
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {screen.account?.primaryAccount}
                  </h2>
                </div>
                
                <div className="text-3xl font-bold text-gray-900 mb-2">
                  {screen.account?.balance}
                </div>
                
                <div className="text-sm text-gray-600 mb-3">
                  Balance
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">
                    {screen.account?.dailyRewards}
                  </span>
                  <span className="text-sm text-green-600 font-medium">
                    {screen.account?.addUsdb}
                  </span>
                </div>
              </motion.div>

              {/* Action Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="grid grid-cols-5 gap-4 mb-6"
              >
                {screen.actions?.map((action) => {
                  const IconComponent = iconMap[action.icon as keyof typeof iconMap] || MoreHorizontal;
                  const isVirtualAccount = action.name === "Virtual Account";
                  
                  return (
                    <motion.button
                      key={action.name}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={isVirtualAccount && onNext ? onNext : undefined}
                      className={`flex flex-col items-center space-y-2 p-2 ${
                        isVirtualAccount ? 'cursor-pointer' : ''
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        isVirtualAccount 
                          ? 'bg-blue-100 border-2 border-blue-500' 
                          : 'bg-gray-100'
                      }`}>
                        <IconComponent className={`w-6 h-6 ${
                          isVirtualAccount ? 'text-blue-600' : 'text-gray-600'
                        }`} />
                      </div>
                      <span className={`text-xs text-center ${
                        isVirtualAccount ? 'text-blue-600 font-medium' : 'text-gray-600'
                      }`}>
                        {action.name}
                      </span>
                    </motion.button>
                  );
                })}
              </motion.div>

              {/* Transaction History */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
                className="flex-1"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Transaction History
                </h3>
                <div className="text-center text-gray-500 py-8">
                  {screen.transactionHistory}
                </div>
              </motion.div>
            </div>
          </PhoneFrame>
        </motion.div>

        {/* Right Side - Info Panel */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="space-y-6"
        >
          <div>
            <h2 className="text-3xl font-bold text-black mb-4">
              {screen.title}
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              {screen.description}
            </p>
            
            <ul className="space-y-2">
              {screen.features?.map((feature, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                  className="flex items-center space-x-2"
                >
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="text-gray-700">{feature}</span>
                </motion.li>
              ))}
            </ul>
          </div>
        </motion.div>
      </div>
    </div>
  );
};
